import { useEffect, useState } from "react";
import ReportUrlModal from "../component/modal/ReportUrlModal";
import "../css/AdBlogManagement.css";
import CampaignPageLayout from "../modules/campaign/components/CampaignPageLayout";
import NotificationModal from "../modules/campaign/components/NotificationModal";
import RefundModal from "../modules/campaign/components/RefundModal";
import adBlogConfig from "../modules/campaign/configs/adBlog.config";
import { useCampaignList } from "../modules/campaign/hooks/useCampaignList";
import { useFilters } from "../modules/campaign/hooks/useFilters";
import { useSelection } from "../modules/campaign/hooks/useSelection";
import { action, submitRefund, saveMemo } from "../modules/campaign/services/campaignService";
import { executeCancel, pointPaymentCancel } from "../modules/wallet/services/walletService";
import { download } from "../modules/campaign/services/downloadService";
import MemoModal from "../modules/campaign/components/MemoModal";
import { useMemoEdit } from "../modules/campaign/hooks/useMemoEdit";
import Layout from "./Layout";

function AdBlogManagement() {
  const filters = useFilters(adBlogConfig.filters.default);
  const { items, summary, loading, refresh } = useCampaignList(adBlogConfig, filters.filter);
  const selection = useSelection(items, adBlogConfig.table.keyField);
  const memoEdit = useMemoEdit({
    saveFn: saveMemo,
    onUpdate: () => {
      refresh();
    },
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedGuidelines, setExpandedGuidelines] = useState(new Set());
  const [expandedPhotoDescriptions, setExpandedPhotoDescriptions] = useState(new Set());
  const [reportMetaId, setReportMetaId] = useState(null);
  const [reportPlaceName, setReportPlaceName] = useState(null);
  const [notificationData, setNotificationData] = useState(null);
  const [refundModalData, setRefundModalData] = useState(null);

  // const productTypes = [
  //   { value: 'blog_press_corps', label: '비실계 배포' },
  //   { value: 'blog_writing', label: '최적블 원고' },
  //   { value: 'realname_reviewers', label: '실명 기자단' },
  //   { value: 'seo_corps_distribution', label: '최적블 배포' },
  //   { value: 'blog_distribution_without_photo', label: '사진필요없는 블로그배포' }
  // ];

  const toggleGuideline = (blogId) => {
    setExpandedGuidelines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(blogId)) {
        newSet.delete(blogId);
      } else {
        newSet.add(blogId);
      }
      return newSet;
    });
  };

  const togglePhotoDescription = (blogId) => {
    setExpandedPhotoDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(blogId)) {
        newSet.delete(blogId);
      } else {
        newSet.add(blogId);
      }
      return newSet;
    });
  };

  const openReportModal = (adCampaignMetaId, type, placeName) => {
    if (!adCampaignMetaId) {
      alert("캠페인 메타 ID가 없습니다.");
      return;
    }
    setReportMetaId(adCampaignMetaId);
    setReportPlaceName(placeName || null);
  };

  const closeReportModal = () => {
    setReportMetaId(null);
    setReportPlaceName(null);
  };

  const openNotificationModal = (data) => {
    if (!data.adCampaignMetaId) {
      alert("캠페인 메타 ID가 없습니다.");
      return;
    }
    setNotificationData(data);
  };

  const closeNotificationModal = () => {
    setNotificationData(null);
  };

  const handleRefundOpen = (selectedIds, allItems) => {
    const keyField = adBlogConfig.table.keyField;
    const selectedItems = allItems
      .filter(item => selectedIds.includes(item[keyField]) && (item.isPaid === 1 || item.isPaid === 2 || item.isRefunded === 1))
      .map(item => ({
        adCampaignMetaId: item.adCampaignMetaId,
        userName: item.userName,
        userId: item.userId,
        paymentType: item.paymentType,
        adPaymentTransactionId: item.adPaymentTransactionId,
        totalAmount: item.totalAmount ?? item.totalCost ?? 0,
        refundedAmount: item.refundedAmount ?? 0,
        remainingAmount: item.remainingAmount ?? item.totalAmount ?? item.totalCost ?? 0,
      }));
    const hasPoint = selectedItems.some(i => i.paymentType === 'point');
    if (hasPoint && selectedItems.length > 1) {
      alert('포인트 결제 건이 포함된 경우 1건씩 환불해주세요.');
      return;
    }
    setRefundModalData({ items: selectedItems });
  };

  const handleRefundSubmit = async (refundItems, reason = '환불 처리') => {
    try {
      const nonPointItems = refundItems.filter(i => i.paymentType !== 'point');
      const pointItems = refundItems.filter(i => i.paymentType === 'point' && i.adPaymentTransactionId);

      if (nonPointItems.length > 0) {
        await submitRefund(adBlogConfig, nonPointItems);
      }

      const txGroups = {};
      for (const item of pointItems) {
        const txId = item.adPaymentTransactionId;
        if (!txGroups[txId]) txGroups[txId] = { userId: item.userId, items: [] };
        txGroups[txId].items.push({ adCampaignMetaId: item.adCampaignMetaId, cancelAmount: item.cancelAmount });
      }
      for (const [txId, { userId, items: cancelItems }] of Object.entries(txGroups)) {
        await executeCancel(Number(txId), cancelItems, reason);
      }
      setRefundModalData(null);
      selection.clearSelection();
      await refresh();
      alert("환불 처리가 완료되었습니다.");
    } catch (error) {
      console.error("환불 처리 오류:", error);
      alert(error.message || "환불 처리 중 오류가 발생했습니다.");
    }
  };

  // 가이드라인과 리포트 버튼에 이벤트 리스너 추가
  useEffect(() => {
    const handleGuidelineClick = (e) => {
      const guidelineBox = e.target.closest(".guideline-textbox");
      if (guidelineBox) {
        const blogId = guidelineBox.getAttribute("data-blog-id");
        if (blogId) {
          toggleGuideline(parseInt(blogId));
        }
      }
    };

    const handleReportClick = (e) => {
      const button = e.target.closest('button[data-campaign-meta-id]:not([data-action="notification"])');
      if (button) {
        const adCampaignMetaId = button.getAttribute("data-campaign-meta-id");
        const type = button.getAttribute("data-type");
        const placeName = button.getAttribute("data-place-name");
        openReportModal(adCampaignMetaId, type, placeName);
      }
    };

    const handleNotificationClick = (e) => {
      const button = e.target.closest('button[data-action="notification"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const userId = Number(button.getAttribute("data-user-id"));
        const productName = button.getAttribute("data-product-name") || "";
        const submittedAt = button.getAttribute("data-submitted-at") || "";
        const purchaseAmount = Number(button.getAttribute("data-purchase-amount")) || 0;
        openNotificationModal({ adCampaignMetaId, userId, productName, submittedAt, purchaseAmount });
      }
    };

    const handlePhotoDescriptionClick = (e) => {
      const box = e.target.closest(".photo-description-textbox");
      if (box) {
        const blogId = box.getAttribute("data-photo-blog-id");
        if (blogId) {
          togglePhotoDescription(parseInt(blogId));
        }
      }
    };

    document.addEventListener("click", handleGuidelineClick);
    document.addEventListener("click", handlePhotoDescriptionClick);
    document.addEventListener("click", handleReportClick);
    document.addEventListener("click", handleNotificationClick, true);

    return () => {
      document.removeEventListener("click", handleGuidelineClick);
      document.removeEventListener("click", handlePhotoDescriptionClick);
      document.removeEventListener("click", handleReportClick);
      document.removeEventListener("click", handleNotificationClick, true);
    };
  }, []);

  useEffect(() => {
    const handleMemoClick = (e) => {
      const button = e.target.closest('button[data-action="memo"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const currentMemo = button.getAttribute("data-memo") || null;
        memoEdit.openModal(adCampaignMetaId, currentMemo || null);
      }
    };
    document.addEventListener("click", handleMemoClick, true);
    return () => document.removeEventListener("click", handleMemoClick, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoEdit.openModal]);

  // 가이드라인 확장 상태 업데이트를 위한 컬럼 수정
  const columnsWithGuideline = adBlogConfig.table.columns.map((column) => {
    if (column.key === "guideline") {
      return {
        ...column,
        render: (row) => {
          const photoItems = Array.isArray(row.photoDescription) ? row.photoDescription : [];
          const isPhotoExpanded = expandedPhotoDescriptions.has(row.adBlogId);
          return (
            <>
              <div
                className={`guideline-textbox ${expandedGuidelines.has(row.adBlogId) ? "expanded" : ""}`}
                data-blog-id={row.adBlogId}
                title={row.guideline || "-"}
              >
                {row.guideline || "-"}
              </div>
              <div
                className={`guideline-textbox photo-description-textbox ${isPhotoExpanded ? "expanded" : ""}`}
                data-photo-blog-id={row.adBlogId}
              >
                {photoItems.length === 0
                  ? "-"
                  : isPhotoExpanded
                  ? photoItems.map((item, i) => <div key={i}>{i + 1}. {item}</div>)
                  : photoItems[0]}
              </div>
            </>
          );
        },
      };
    }
    if (column.key === "report") {
      return {
        ...column,
        render: (row) => (
          <button
            type="button"
            className="status-btn submitted"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-type={row.type}
            data-place-name={row.placeName || ""}
          >
            리포트
          </button>
        ),
      };
    }
    return column;
  });

  const configWithCustomColumns = {
    ...adBlogConfig,
    table: {
      ...adBlogConfig.table,
      columns: columnsWithGuideline,
    },
  };

  const handleFilterChange = (name, value) => {
    filters.updateFilter(name, value);
  };

  const handleFilterApply = () => {
    filters.applyFilter();
    selection.clearSelection();
  };

  const handleFilterReset = () => {
    filters.resetFilter();
    selection.clearSelection();
  };

  const handleSelect = (id) => {
    selection.toggleId(id);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      selection.selectAll();
    } else {
      selection.clearSelection();
    }
  };

  const handlePageChange = (page) => {
    filters.setPage(page);
  };

  const handleAction = async (actionType, payload, actionConfig) => {
    try {
      const result = await action(adBlogConfig, actionType, payload);

      if (result.status === "success") {
        let message = "처리되었습니다.";
        if (actionType === "submitted") {
          message = payload.toggleValue === 1 ? "접수 완료 처리되었습니다." : "접수 취소 처리되었습니다.";
        } else if (actionType === "paid") {
          message = "결제 완료 처리되었습니다.";
        } else if (actionType === "refund") {
          message = "환불 처리되었습니다.";
        } else if (actionType === "complete") {
          message = payload.toggleValue === 1 ? "완료 처리되었습니다." : "취소 처리되었습니다.";
        } else if (actionType === "delete") {
          message = "삭제되었습니다.";
        }
        alert(message);

        selection.clearSelection();
        await refresh();
      } else {
        alert("처리 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("액션 처리 오류:", error);
      alert(error.message || "처리 중 오류가 발생했습니다.");
    }
  };

  const handleDownload = async (selectedIds, allItems) => {
    try {
      setIsDownloading(true);

      const selectedRows =
        selectedIds.length > 0
          ? allItems.filter((item) => selectedIds.includes(item[adBlogConfig.table.keyField]))
          : allItems;

      await download(adBlogConfig, selectedRows, allItems);
      alert("다운로드가 완료되었습니다.");
    } catch (error) {
      console.error("다운로드 오류:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const transformedSummary = {
    totalCount: summary.total || summary.totalCount || 0,
    waitingCount: summary.waiting || summary.waitingCount || 0,
    processingCount: summary.processing || summary.processingCount || 0,
    completedCount: summary.completed || summary.completedCount || 0,
  };

  return (
    <Layout>
      <CampaignPageLayout
        config={configWithCustomColumns}
        data={{ items, summary: transformedSummary }}
        filter={filters.filter}
        selectedIds={selection.selectedIds}
        loading={loading || isDownloading}
        handlers={{
          tempFilter: filters.tempFilter,
          onFilterChange: handleFilterChange,
          onFilterApply: handleFilterApply,
          onFilterReset: handleFilterReset,
          onSelect: handleSelect,
          onSelectAll: handleSelectAll,
          onPageChange: handlePageChange,
          onAction: handleAction,
          onDownload: handleDownload,
          onRefundOpen: handleRefundOpen,
        }}
      />

      {reportMetaId && (
        <ReportUrlModal
          adCampaignMetaId={reportMetaId}
          placeName={reportPlaceName}
          onClose={closeReportModal}
          onSaved={refresh}
        />
      )}

      <NotificationModal
        isOpen={notificationData !== null}
        adCampaignMetaId={notificationData?.adCampaignMetaId}
        userId={notificationData?.userId}
        productName={notificationData?.productName}
        submittedAt={notificationData?.submittedAt}
        purchaseAmount={notificationData?.purchaseAmount}
        onClose={closeNotificationModal}
        onSuccess={refresh}
      />

      <RefundModal
        isOpen={refundModalData !== null}
        items={refundModalData?.items || []}
        onClose={() => setRefundModalData(null)}
        onSubmit={handleRefundSubmit}
      />

      <MemoModal
        isOpen={memoEdit.isOpen}
        id={memoEdit.memoData?.id}
        currentMemo={memoEdit.memoData?.currentMemo}
        loading={memoEdit.loading}
        onSave={memoEdit.save}
        onDelete={memoEdit.deleteMemo}
        onClose={memoEdit.closeModal}
      />
    </Layout>
  );
}

export default AdBlogManagement;
